import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslationService } from '@spartacus/core';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { initialStatus, loadingStatus } from '../../../core/models/request-status.model';
import { Option } from '../../../core/models/option.model';
import { TableProConfig } from '../../../components/table-pro/core/table-pro.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CustomValidatorService } from '../../../components/form-elements/custom-validator.service';
import { ReturnsStatusRequest } from './core/return.model';
import { ReturnService } from './core/return.service';
import { ExcelDownloadService } from '../../../core/services/excelDownload.service';

@Component({
  selector: 'app-returns',
  templateUrl: './returns.component.html',
  styleUrl: './returns.component.scss',
  standalone: false,
})
export class ReturnsComponent implements OnInit, OnDestroy {
  subscription = new Subscription();
  returnsRequest$ = new BehaviorSubject<ReturnsStatusRequest>({
    ...initialStatus,
  });
  options$ = new BehaviorSubject<Option[]>([]);

  tableConfig: TableProConfig = {
    collumnConfig: [
      { id: 'order', index: 0, type: 'text' },
      { id: 'po', index: 1, type: 'text' },
      { id: 'line', index: 2, type: 'text' },
      { id: 'product', index: 3, type: 'text' },
      { id: 'date', index: 4, type: 'date' },
      { id: 'qtyAuthorized', index: 5, type: 'text' },
      { id: 'paymentTerm', index: 6, type: 'text' },
      { id: 'qtyAccepted', index: 7, type: 'text' },
      { id: 'sellingAmount', index: 8, type: 'text' },
    ],
  };

  minDate = new Date(new Date().setDate(new Date().getDate() - 365));
  maxDate = new Date();

  form: FormGroup = this.fb.group({
    searchBy: ['orderDate'],
    searchValue: [null],
    searchType: ['90'],
    fromDate: [new Date(new Date().setDate(new Date().getDate() - 90))],
    toDate: [this.maxDate],
    onlyOpen: [false, []],
    poNumber: [null],
    invoiceNumber: [null],
    orderNumber: [null],
    partNumber: [null],
  });

  constructor(
    private returnService: ReturnService,
    protected fb: FormBuilder,
    private customValidatorService: CustomValidatorService,
    private excelDownloadService: ExcelDownloadService,
    private translationService: TranslationService,
  ) {}

  ngOnInit(): void {
    this.listenToSearchBy();
    this.listenerSearchType();
    this.loadOptions();
    this.submit();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  backSearchType() {
    this.form.get('searchType')?.setValue('90');
  }

  handlerKeydownShipDate(event: KeyboardEvent) {
    const key = event.key.toLocaleLowerCase();

    if (key !== 'backspace' && key !== 'delete') {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  listenToSearchBy(): void {
    this.subscription.add(
      this.form.get('searchBy')?.valueChanges.subscribe(searchBy => {
        this.resetFormFields();
        if (searchBy === 'orderDate') {
          this.form.get('searchType')?.setValue('90');
          this.setDaysToSearchByQuantity('90');
        } else {
          this.form.get('fromDate')?.setValue(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
          this.form.get('toDate')?.setValue(this.maxDate);
          this.form.get('onlyOpen')?.setValue(false);
        }
      }),
    );
  }

  listenerSearchType() {
    this.subscription.add(
      this.form.get('searchType')?.valueChanges.subscribe(value => {
        if (value === 'custom') {
          this.evaluateValidation(value as string);
        } else {
          this.setDaysToSearchByQuantity(value as string);
        }
      }),
    );
  }

  loadOptions(): void {
    this.subscription.add(
      combineLatest([
        this.translationService.translate('formSearch.dateRangeOptions.last30Days'),
        this.translationService.translate('formSearch.dateRangeOptions.last60Days'),
        this.translationService.translate('formSearch.dateRangeOptions.last90Days'),
        this.translationService.translate('formSearch.dateRangeOptions.customRange'),
      ]).subscribe(([last30Days, last60Days, last90Days, customRange]) => {
        this.options$.next([
          { code: '30', label: last30Days },
          { code: '60', label: last60Days },
          { code: '90', label: last90Days },
          { code: 'custom', label: customRange },
        ]);
      }),
    );
  }

  resetFormFields(): void {
    const fields = ['invoiceNumber', 'poNumber', 'orderNumber', 'partNumber'];
    fields.forEach(field => this.form.get(field)?.setValue(null));
    this.form.get('searchValue')?.setValue(null);
  }

  evaluateValidation(searchType: string) {
    const removeValidations = (control: string) => {
      this.form.get(control)?.clearValidators();
      this.form.get(control)?.updateValueAndValidity();
    };

    const addValidations = (control: string) => {
      this.form.get(control)?.setValidators([this.customValidatorService.required()]);
      this.form.get(control)?.updateValueAndValidity();
    };

    const fields = ['fromDate', 'toDate'];

    fields.forEach(field => removeValidations(field));

    if (searchType === 'custom') {
      fields.forEach(field => addValidations(field));
    }
  }

  setDaysToSearchByQuantity(days: string) {
    const qtyDays = parseInt(days);

    this.form.get('fromDate')?.setValue(new Date(new Date().setDate(new Date().getDate() - qtyDays)));
    this.form.get('toDate')?.setValue(this.maxDate);
  }

  submit() {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      return this.getReturns();
    }
  }

  onEnter(event: Event) {
    event.preventDefault();
    this.submit();
  }

  getReturns() {
    const query = this.buildQuery();

    this.returnsRequest$.next({ ...loadingStatus });

    this.subscription.add(
      this.returnService.getReturns(query).subscribe(response => {
        this.returnsRequest$.next(response);
      }),
    );
  }

  exportReturns() {
    const query = this.buildQuery();

    this.subscription.add(
      this.returnService.exportReturns(query).subscribe(response => {
        if (response && response.fileContentBase64) {
          this.excelDownloadService.downloadBase64File(response);
        }
      }),
    );
  }

  buildQuery(): any {
    const { searchBy, searchValue, fromDate, toDate } = this.form.value;

    if (searchBy === 'orderDate') {
      const formattedFromDate = new Date(fromDate).toISOString().split('T')[0];
      const formattedToDate = new Date(toDate).toISOString().split('T')[0];
      return {
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        onlyOpen: this.form.get('onlyOpen')?.value || false,
      };
    }

    return {
      fromDate: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0],
      toDate: new Date().toISOString().split('T')[0],
      onlyOpen: this.form.get('onlyOpen')?.value || false,
      poNumber: searchBy === 'poNumber' ? searchValue : null,
      invoiceNumber: searchBy === 'invoiceNumber' ? searchValue : null,
      orderNumber: searchBy === 'orderNumber' ? searchValue : null,
      catalogNumber: searchBy === 'catalogNumber' ? searchValue : null,
      partNumber: searchBy === 'partNumber' ? searchValue : null,
      customerPartNumber: searchBy === 'customerPartNumber' ? searchValue : null,
    };
  }

  clearInputValue() {
    this.form.patchValue({ searchValue: null });
  }

  clearAll(): void {
    window.location.reload();
  }
}
